/**
 * /work-orders — lista + tablero por estado.
 */
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { LayoutList, Columns3, Search, Wrench } from 'lucide-react'
import { PageHeader } from '@/components/app-shell'
import { Card } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { StatusPill } from '@/components/ui/status-pill'
import { listWorkOrders, patchWorkOrder, type WorkOrder } from '@/lib/api'
import { cn } from '@/lib/utils'

const BOARD_COLUMNS = [
  'Reported',
  'Scheduled',
  'In Progress',
  'Waiting for Parts',
  'Waiting for Approval',
  'Completed',
] as const

export function WorkOrdersPage() {
  const [view, setView] = useState<'list' | 'board'>('list')
  const [q, setQ] = useState('')
  const [status, setStatus] = useState('')
  const [priority, setPriority] = useState('')
  const qc = useQueryClient()

  const query = useQuery({
    queryKey: ['work-orders', q, status, priority],
    queryFn: () =>
      listWorkOrders({
        q: q || undefined,
        status: status || undefined,
        priority: priority || undefined,
      }),
  })

  const move = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      patchWorkOrder(id, { status }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['work-orders'] })
    },
  })

  const rows = query.data?.workOrders ?? []
  const byStatus = useMemo(() => {
    const map = new Map<string, WorkOrder[]>()
    for (const col of BOARD_COLUMNS) map.set(col, [])
    for (const wo of rows) {
      const list = map.get(wo.status) ?? []
      list.push(wo)
      map.set(wo.status, list)
    }
    return map
  }, [rows])

  return (
    <>
      <PageHeader
        title="Work orders"
        subtitle={`${rows.length} orden${rows.length === 1 ? '' : 'es'}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/work-orders/new"
              className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-primary px-3 text-sm font-medium text-primary-foreground"
            >
              New WO
            </Link>
            <div className="flex rounded-lg border border-border p-0.5">
              <button
                type="button"
                onClick={() => setView('list')}
                className={cn(
                  'inline-flex min-h-9 cursor-pointer items-center gap-1.5 rounded-md px-3 text-sm',
                  view === 'list' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground',
                )}
              >
                <LayoutList className="size-4" aria-hidden />
                Lista
              </button>
              <button
                type="button"
                onClick={() => setView('board')}
                className={cn(
                  'inline-flex min-h-9 cursor-pointer items-center gap-1.5 rounded-md px-3 text-sm',
                  view === 'board' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground',
                )}
              >
                <Columns3 className="size-4" aria-hidden />
                Tablero
              </button>
            </div>
          </div>
        }
      />

      <div className="mb-4 flex flex-col gap-3 sm:flex-row">
        <label className="relative min-w-0 flex-1">
          <span className="sr-only">Buscar</span>
          <Search
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="WO, título, unidad…"
            className="h-11 w-full rounded-xl border border-border bg-card pr-3 pl-10 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </label>
        <select
          aria-label="Estado"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="h-11 rounded-xl border border-border bg-card px-3 text-sm"
        >
          <option value="">Todos los estados</option>
          {[...BOARD_COLUMNS, 'Cancelled'].map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          aria-label="Prioridad"
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
          className="h-11 rounded-xl border border-border bg-card px-3 text-sm"
        >
          <option value="">Todas las prioridades</option>
          {['Low', 'Normal', 'High', 'Critical'].map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </div>

      {query.isLoading ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState
            icon={Wrench}
            title="Sin órdenes"
            description="Importa el Excel o crea órdenes desde el detalle de flota."
          />
        </Card>
      ) : view === 'list' ? (
        <Card className="divide-y divide-border overflow-hidden">
          {rows.map((wo) => (
            <Link
              key={wo.id}
              to="/work-orders/$workOrderId"
              params={{ workOrderId: wo.id }}
              className="flex min-h-[60px] items-center gap-3 px-4 py-3 transition-colors hover:bg-muted"
            >
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium">{wo.title}</span>
                <span className="block text-xs text-muted-foreground">
                  Unit {wo.unitNumber} · {wo.workOrderNumber}
                </span>
              </span>
              <StatusPill status={wo.priority} />
              <StatusPill status={wo.status} />
            </Link>
          ))}
        </Card>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-2">
          {BOARD_COLUMNS.map((col) => {
            const list = byStatus.get(col) ?? []
            return (
              <div
                key={col}
                className="w-72 shrink-0 rounded-2xl border border-border bg-muted/40"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault()
                  const id = e.dataTransfer.getData('text/wo-id')
                  if (id) move.mutate({ id, status: col })
                }}
              >
                <div className="flex items-center justify-between px-3 py-2.5">
                  <p className="text-sm font-semibold">{col}</p>
                  <span className="text-xs text-muted-foreground">{list.length}</span>
                </div>
                <div className="space-y-2 px-2 pb-2">
                  {list.map((wo) => (
                    <Link
                      key={wo.id}
                      to="/work-orders/$workOrderId"
                      params={{ workOrderId: wo.id }}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('text/wo-id', wo.id)
                        e.dataTransfer.effectAllowed = 'move'
                      }}
                      className="block cursor-grab rounded-xl border border-border bg-card p-3 shadow-card transition-colors hover:bg-card/80 active:cursor-grabbing"
                    >
                      <p className="text-sm font-medium">{wo.title}</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Unit {wo.unitNumber} · {wo.workOrderNumber}
                      </p>
                      <div className="mt-2">
                        <StatusPill status={wo.priority} />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </>
  )
}
